import type { FilterCategory } from '../shared/components/filter-modal/filter-modal.component';

/**
 * CWS-2051: Notifications "Event" filter.
 *
 * The filter is an exclusion list: checked events are hidden, so the default (nothing
 * checked) shows everything and new event types are never hidden by accident. The
 * shared filter modal's "Clear Filters" unchecks every option, which maps naturally
 * to "show all".
 *
 * Matching is on the stored `lpr_entries.event` slug (server-side `exclude_events`).
 */

export const NOTIFICATION_EVENT_FILTER_CATEGORY_ID = 'hideEvents';

export interface NotificationEventFilterOption {
  /** Label shown in the modal; matches the Event column wording. */
  label: string;
  /** Raw `lpr_entries.event` slugs this option hides. */
  events: readonly string[];
}

/** One row per label the Event column shows; aliases sharing a label are grouped. */
export const NOTIFICATION_EVENT_FILTER_OPTIONS: readonly NotificationEventFilterOption[] = [
  { label: 'Active Subscription', events: ['active-subscription'] },
  { label: 'Order Success', events: ['order-success'] },
  { label: 'Order Completed', events: ['order-completed'] },
  { label: 'New Customer Request', events: ['invalid-license-plate', 'create-customer-request'] },
  { label: 'New Order Request', events: ['create-order-request'] },
  { label: 'Payment Request', events: ['create-payment-request'] },
  { label: 'Paused Subscription', events: ['paused-subscription'] },
  { label: 'Exit Active Subscription', events: ['exit-active-subscription'] },
  { label: 'Order Not Found', events: ['order-not-found'] },
  { label: 'Vehicle Not Found', events: ['vehicle-not-found'] },
  { label: 'Fleet Vehicle Found', events: ['fleet-vehicle-found'] },
  { label: 'Fleet Invoice Added', events: ['fleet-invoice-added'] },
  { label: 'Detailing Appointment Found', events: ['detailing-appointment-found'] },
  { label: 'Exit Detailing Appointment Found', events: ['exit-detailing-appointment-found'] },
  { label: 'Prepaid Redemption', events: ['redeem-prepaid-purchase'] },
  { label: 'Rewash', events: ['rewash'] },
  { label: 'Select Package', events: ['multi-eligible-selection'] },
  { label: 'Way Active Subscription', events: ['way-active-subscription'] },
  { label: 'Way Single Wash', events: ['way-single-wash'] },
  { label: 'Way Purchase Selection', events: ['way-purchase-selection'] },
  { label: 'Way Purchase Unredeemable', events: ['way-purchase-unredeemable'] },
  {
    label: 'Way Subscription Already Redeemed Today',
    events: ['way-subscription-already-redeemed-today'],
  },
];

function optionValue(option: NotificationEventFilterOption): string {
  return option.events.join(',');
}

/**
 * Fresh filter-modal categories for the current exclusion list. A new object each call:
 * the modal mutates `selected` in place, so Cancel must not leak into page state.
 */
export function buildNotificationEventFilterCategories(
  excludedEvents: readonly string[]
): FilterCategory[] {
  const excluded = new Set(excludedEvents);
  return [
    {
      id: NOTIFICATION_EVENT_FILTER_CATEGORY_ID,
      name: 'Hide events',
      type: 'checkbox',
      options: NOTIFICATION_EVENT_FILTER_OPTIONS.map(option => ({
        id: optionValue(option),
        label: option.label,
        value: optionValue(option),
        selected: option.events.some(event => excluded.has(event)),
      })),
    },
  ];
}

/** Raw event slugs to exclude, read back from the modal's applied categories. */
export function readExcludedEventsFromCategories(categories: readonly FilterCategory[]): string[] {
  const category = categories.find(cat => cat.id === NOTIFICATION_EVENT_FILTER_CATEGORY_ID);
  if (!category) {
    return [];
  }
  const events = category.options
    .filter(option => option.selected)
    .flatMap(option => option.value.split(','))
    .map(event => event.trim())
    .filter(Boolean);
  return [...new Set(events)];
}

/** Count of checked options (badge on the Filter button), not raw slugs. */
export function countActiveEventFilterOptions(excludedEvents: readonly string[]): number {
  const excluded = new Set(excludedEvents);
  return NOTIFICATION_EVENT_FILTER_OPTIONS.filter(option =>
    option.events.some(event => excluded.has(event))
  ).length;
}

/** `exclude_events` query value, or null when nothing is excluded. */
export function toExcludeEventsParam(excludedEvents: readonly string[]): string | null {
  const cleaned = excludedEvents.map(event => String(event ?? '').trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(',') : null;
}

/**
 * Raw stored event of a live (SSE/Pusher) notification. Mirrors what the list API filters
 * on: when the row carries an `event` field (even null) that stored value wins, so a
 * NULL-event row stays visible like the server's `event IS NULL` branch. The notification
 * type is only a fallback for payloads that carry no `event` field at all.
 */
export function getRawLprEventFromNotification(
  notification: { type?: string | null; data?: unknown } | null | undefined
): string {
  const data = (notification?.data ?? {}) as Record<string, unknown>;
  const nested = (data['data'] ?? {}) as Record<string, unknown>;
  let raw: unknown;
  if ('event' in data) {
    raw = data['event'];
  } else if ('event' in nested) {
    raw = nested['event'];
  } else {
    raw = notification?.type;
  }
  return String(raw ?? '').trim();
}

/** True when a live row should stay visible under the current exclusion list. */
export function notificationPassesEventFilter(
  notification: { type?: string | null; data?: unknown } | null | undefined,
  excludedEvents: readonly string[]
): boolean {
  if (excludedEvents.length === 0) {
    return true;
  }
  const event = getRawLprEventFromNotification(notification);
  return !event || !excludedEvents.includes(event);
}
